// Instagram and Facebook direct on Meta's APIs in the app (step 0.12; docs/specs/META_DIRECT.md 3.1). Types for
// crm_meta_accounts, labels, the choice of which accounts Ogma posts to (social.networks entries with publisher meta),
// and Facebook Login for Business in the browser.
//
// Facebook Login for Business uses the same JavaScript SDK as WhatsApp's Embedded Signup (lib/whatsapp.ts): FB.login(
// callback, {config_id, response_type: "code", override_default_response_type: true}); the callback gets
// authResponse.code, which the act function exchanges on the server. UNVERIFIED for this configuration type: that the
// same options apply to a Login for Business configuration for Pages and Instagram (Meta's WhatsApp pages were checked,
// the Login for Business pages could not be read).

import { loadSdk, type Win } from "./whatsapp";
import type { SocialNetworkEntry } from "./settingsForms";

export type MetaAccountStatus = "connected" | "needs_reconnect" | "disconnected";

export interface MetaAccount {
  id: string;
  kind: "facebook_page" | "instagram";
  external_id: string;
  name: string | null;
  username?: string | null;
  page_id?: string | null;
  login: "facebook" | "instagram";
  token_expires_at?: string | null;
  scopes?: string[] | null;
  status: MetaAccountStatus;
  checked_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface MetaLoginConfig {
  app_id: string;
  config_id: string;
  graph_version: string;
  /** Demo mode: no Meta window, the connection is simulated. */
  demo?: boolean;
}

export interface MetaConnectResult {
  accounts: MetaAccount[];
  token_kind?: "user" | "system_user";
  token_expires_at?: string | null;
  missing_scopes?: string[];
}

export const META_STATUS_LABEL: Record<MetaAccountStatus, string> = {
  connected: "Connected",
  needs_reconnect: "Needs reconnecting: press Connect again",
  disconnected: "Disconnected",
};

/** crm_meta_accounts as a list, whether the reader answers with a list or {accounts: [...]}. */
export function readMetaAccounts(v: unknown): MetaAccount[] {
  const list = Array.isArray(v) ? v : v && typeof v === "object" && Array.isArray((v as { accounts?: unknown }).accounts) ? (v as { accounts: unknown[] }).accounts : [];
  return list.filter((a): a is MetaAccount => !!a && typeof a === "object" && typeof (a as MetaAccount).external_id === "string");
}

/** crm_meta_accounts with who may change them: canManage is true for an owner of the business (review fix: members see
 * the accounts read-only). An older answer without the flag counts as an owner's, as before. */
export function readMetaAccountsView(v: unknown): { accounts: MetaAccount[]; canManage: boolean } {
  const flag = v && typeof v === "object" && !Array.isArray(v) ? (v as { can_manage?: unknown }).can_manage : undefined;
  return { accounts: readMetaAccounts(v), canManage: flag !== false };
}

/** The Instagram Login return (review fix): ?instagram=pending&n=<nonce> on Settings › Connections. Null otherwise. */
export function pendingInstagramNonce(search: string): string | null {
  const q = new URLSearchParams(search);
  const n = q.get("n");
  return q.get("instagram") === "pending" && n && /^[A-Za-z0-9_-]{16,64}$/.test(n) ? n : null;
}

export function accountLabel(a: Pick<MetaAccount, "kind" | "name" | "username" | "external_id">): string {
  if (a.kind === "instagram") return a.username ? `@${a.username}` : a.name ?? a.external_id;
  return a.name ?? a.external_id;
}

export function kindLabel(a: Pick<MetaAccount, "kind" | "login">): string {
  return a.kind === "instagram" ? (a.login === "instagram" ? "Instagram (no Facebook Page)" : "Instagram") : "Facebook Page";
}

/** The social.networks entry that posts to this account through Meta. */
export function metaEntry(a: MetaAccount): SocialNetworkEntry {
  return a.kind === "instagram"
    ? { network: "instagram", account: "business", publisher: "meta", ig_user_id: a.external_id, name: accountLabel(a) }
    : { network: "facebook", account: "page", publisher: "meta", meta_page_id: a.external_id, name: accountLabel(a) };
}

/** True when this entry posts to this account through Meta. */
export function entryUses(n: SocialNetworkEntry, a: Pick<MetaAccount, "kind" | "external_id">): boolean {
  if (n.publisher !== "meta") return false;
  return a.kind === "instagram" ? n.network === "instagram" && n.ig_user_id === a.external_id : n.network === "facebook" && n.meta_page_id === a.external_id;
}

/** The accounts the current settings post to through Meta (their external ids). */
export function chosenIds(networks: SocialNetworkEntry[], accounts: MetaAccount[]): string[] {
  return accounts.filter((a) => networks.some((n) => entryUses(n, a))).map((a) => a.external_id);
}

/** What is wrong with a choice before saving: Ogma posts to one Instagram account and one Facebook Page per business. */
export function metaPicksProblem(accounts: MetaAccount[], chosen: string[]): string | null {
  const picked = accounts.filter((a) => chosen.includes(a.external_id));
  if (picked.some((a) => a.status !== "connected")) return "Reconnect the accounts marked as needing it before Ogma posts to them.";
  if (picked.filter((a) => a.kind === "instagram").length > 1) return "Choose one Instagram account: Ogma posts to one Instagram account per business.";
  if (picked.filter((a) => a.kind === "facebook_page").length > 1) return "Choose one Facebook Page: Ogma posts to one Page per business.";
  return null;
}

/**
 * The new social.networks: every Meta entry is replaced by the chosen accounts; a Buffer entry on the same network and
 * account as a chosen one gives way to it (keeping its posts a week); every other entry stays as it is.
 */
export function networksWithMeta(existing: SocialNetworkEntry[], accounts: MetaAccount[], chosen: string[]): SocialNetworkEntry[] {
  const add = accounts.filter((a) => chosen.includes(a.external_id)).map((a) => {
    const entry = metaEntry(a);
    const before = existing.find((n) => entryUses(n, a)) ?? existing.find((n) => n.network === entry.network && n.account === entry.account);
    return typeof before?.cadence_per_week === "number" ? { ...entry, cadence_per_week: before.cadence_per_week } : entry;
  });
  const taken = new Set(add.map((n) => `${n.network}/${n.account}`));
  const keep = existing.filter((n) => n.publisher !== "meta" && !taken.has(`${n.network}/${n.account}`));
  return [...keep, ...add];
}

/** The social.networks without any entry that posts to this account (after a disconnect). Null when none did. */
export function networksWithout(existing: SocialNetworkEntry[], a: Pick<MetaAccount, "kind" | "external_id">): SocialNetworkEntry[] | null {
  const next = existing.filter((n) => !entryUses(n, a));
  return next.length === existing.length ? null : next;
}

/**
 * Opens Facebook Login for Business with Ogma's configuration and resolves with the code (it lasts seconds). Rejects
 * when the person closes the window.
 */
export async function launchMetaLogin(cfg: MetaLoginConfig, win: Win = window as Win): Promise<string> {
  if (!cfg.app_id || !cfg.config_id) throw new Error("Connecting Instagram and Facebook isn't switched on for this server yet.");
  const fb = await loadSdk(win, cfg.app_id, cfg.graph_version);
  return new Promise<string>((resolve, reject) => {
    fb.login((r) => {
      const c = r?.authResponse?.code;
      if (typeof c === "string" && c) resolve(c);
      else reject(new Error("The Facebook window was closed before it finished. Press Connect Instagram and Facebook to try again."));
    }, { config_id: cfg.config_id, response_type: "code", override_default_response_type: true });
  });
}
